import React from 'react';
import {
  Box,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip as MuiTooltip,
  Typography,
} from '@mui/material';
import { formatDateOnly } from '../utils/formatDateOnly';
import type { VitalTableRow } from '../utils/ehrChanges';

/**
 * PHA-1142: the readings behind one care-plan goal — "each vital pulled from EHR on its own
 * row, with date, latest on top", the reading the goal's target was calculated from pinned as
 * the first row, and who acknowledged each one.
 *
 * Read-only when `onAcknowledge` is absent (the draft care plan review shows only what the
 * target was calculated from; acknowledging happens on the chart).
 */
export interface GoalVitalsTableProps {
  rows: VitalTableRow[];
  onAcknowledge?: (rows: VitalTableRow[]) => void;
  /** Row keys with an acknowledgment in flight. */
  busyKeys?: Set<string>;
  emptyText?: string;
  dense?: boolean;
}

const formatTimestamp = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleDateString();
};

const GoalVitalsTable: React.FC<GoalVitalsTableProps> = ({
  rows,
  onAcknowledge,
  busyKeys,
  emptyText = 'No readings from the EHR yet.',
  dense = true,
}) => {
  if (!rows || rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
        {emptyText}
      </Typography>
    );
  }
  const pendingRows = rows.filter((r) => r.pending.length > 0);
  const anyBusy = pendingRows.some((r) => busyKeys?.has(r.key));

  return (
    <Box>
      {onAcknowledge && pendingRows.length > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
          <Button size="small" variant="outlined" disabled={anyBusy} onClick={() => onAcknowledge(pendingRows)}>
            Acknowledge all ({pendingRows.length})
          </Button>
        </Box>
      )}
      <Table size={dense ? 'small' : 'medium'}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '18%' }}>Date</TableCell>
            <TableCell sx={{ width: '32%' }}>Reading</TableCell>
            <TableCell sx={{ width: '14%' }}>Source</TableCell>
            <TableCell sx={{ width: '36%' }}>Acknowledged</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const isPending = row.pending.length > 0;
            return (
              <TableRow
                key={row.key}
                sx={{
                  bgcolor: isPending ? '#fff8e1' : row.isBaseline ? 'action.hover' : 'inherit',
                  borderLeft: isPending ? '3px solid #ed6c02' : 'none',
                }}
              >
                <TableCell>{formatDateOnly(row.date)}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: row.isBaseline ? 600 : 400 }}>
                      {row.display}
                    </Typography>
                    {row.isBaseline && (
                      <MuiTooltip title="The reading this goal's target was calculated from" placement="top">
                        <Chip label="Used for target" size="small" color="primary" variant="outlined" />
                      </MuiTooltip>
                    )}
                    {isPending && <Chip label="NEW" size="small" color="warning" />}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {row.source === 'ehr' ? 'EHR' : 'Entered'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {isPending ? (
                    onAcknowledge ? (
                      <Button
                        size="small"
                        variant="contained"
                        color="warning"
                        disabled={busyKeys?.has(row.key)}
                        onClick={() => onAcknowledge([row])}
                      >
                        {busyKeys?.has(row.key) ? 'Saving…' : 'Acknowledge'}
                      </Button>
                    ) : (
                      <Typography variant="body2" color="warning.main">
                        Awaiting acknowledgment
                      </Typography>
                    )
                  ) : row.acknowledgment ? (
                    <MuiTooltip
                      title={
                        row.acknowledgment.via === 'ehr_sync_run'
                          ? 'Acknowledged together with the rest of that EHR update'
                          : ''
                      }
                      placement="top"
                    >
                      <Typography variant="body2">
                        {row.acknowledgment.by_name || row.acknowledgment.by || 'Acknowledged'}
                        {row.acknowledgment.ts ? ` · ${formatTimestamp(row.acknowledgment.ts)}` : ''}
                      </Typography>
                    </MuiTooltip>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {row.isBaseline ? 'Reviewed at care plan creation' : '—'}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};

export default GoalVitalsTable;
